import {action, computed, observable, reaction} from 'mobx'
import {v4} from 'uuid'

import type {Client} from '/client'
import type {DomainStore} from '/app-domain'

import {listen} from '/shared/utils'

import {Matcher, ParamsType} from './matcher'
import {reorient} from './history-stack'

export type StandardDeps = {client: Client, domain: DomainStore}

export {Route, Overlay, RouterService as Service}

abstract class Route<Model> {
    abstract createModel(deps: StandardDeps): Model
    abstract paths: Record<string, (params: ParamsType) => void>
    abstract readonly url: string

    public constructor(public router: RouterService) {}

    protected get deps() {
        if (!this.router.deps)
            throw new Error('Router not running!')
        return this.router.deps
    }

    private _model: Model | null = null
    get model(): Model {
        if (!this._model)
            this._model = this.createModel(this.deps)
        return this._model
    }

    @computed
    get isOpened(): boolean {
        return this.router.isOpened(this)
    }

    openPage(isRedirect = false) {
        this.router.openPage(this, isRedirect)
    }
}

type RouteData = {
    route: Route<any>
    component: React.ComponentType<{model: any, route?: Route<any>}>
}

class Overlay<Model, Args extends unknown[], MC extends {new(deps: StandardDeps, ...args: Args): Model}> {
    id = v4()

    public constructor(
        private router: RouterService,
        private ModelClass: MC,
        public component: React.ComponentType<{model: Model, overlay?: Overlay<Model, Args, MC>}>
    ) {}

    @observable.ref
    model: Model | null = null

    @computed
    get isOpened(): boolean {
        return !!this.model
    }

    open = (...args: Args): Model => {
        if (!this.router.deps)
            throw new Error('Router not running!')
        this.model = new this.ModelClass(this.router.deps, ...args)
        return this.model
    }

    close = (): void => {
        this.model = null
    }
}

class CommonOverlay<Model, Args extends unknown[], MC extends {new(...args: Args): Model}> {
    id = v4()

    public constructor(
        private ModelClass: MC,
        public component: React.ComponentType<{model: Model, overlay?: CommonOverlay<Model, Args, MC>}>
    ) {}

    @observable.ref
    model: Model | null = null

    @computed
    get isOpened(): boolean {
        return !!this.model
    }

    open = (...args: Args): Model => {
        this.model = new this.ModelClass(...args)
        return this.model
    }

    close = (): void => {
        this.model = null
    }
}

class RouterService {
    @observable.ref page: RouteData | null = null

    @observable commonOverlays: Set<CommonOverlay<any, any, any>> = new Set()
    @observable overlays: Set<Overlay<any, any, any>> = new Set()

    private pages: Map<Route<any>, RouteData> = new Map()

    deps: null | StandardDeps = null
    private _stop: null | Array<() => void> = null

    private matcher = new Matcher()

    get running() {
        return !!this._stop
    }

    backHandler: null | (() => boolean) = null

    onBack(backHandler: null | (() => boolean)) {
        this.backHandler = backHandler
    }

    @action
    start(deps: StandardDeps) {
        this.deps = deps
        reorient()
        void this.matcher.match(location.pathname)

        let skip = false
        this._stop = [
            () => this.deps = null,
            listen(window, 'popstate', (ev) => {
                if (ev.type === 'popstate') {
                    const direction = reorient()
                    if (skip) {
                        skip = false
                        return
                    }
                    const preserve = this.backHandler?.()
                    if (preserve) {
                        skip = true
                        ev.preventDefault()
                        history.go(-direction)
                    } else
                        void this.matcher.match(location.pathname)
                }
            }),
            listen(window, 'goto', (ev: CustomEvent) => {
                void this.matcher.match(ev.detail)
            }),
            reaction(() => this.page?.route.url, url => {
                if (this.matcher.handling || url === undefined || location.pathname === url)
                    return
                this.setUrl(url)
            }, {fireImmediately: true}),
        ]
    }

    private setUrl(url: string, replace = false) {
        if (!replace)
            history.pushState(null, '', url)
        else
            history.replaceState(null, '', url)
        reorient()
    }

    stop() {
        if (!this._stop)
            return
        for (const f of this._stop)
            f()
        this._stop = null
    }

    createOverlay<Model, ModelArgs extends unknown[]> (
        ModelClass: {new(deps: StandardDeps, ...args: ModelArgs): Model},
        component: React.ComponentType<{model: Model}>
    ): Overlay<Model, ModelArgs, typeof ModelClass> {
        const overlay = new Overlay<Model, ModelArgs, typeof ModelClass>(this, ModelClass, component)
        this.overlays.add(overlay)
        return overlay
    }

    createCommonOverlay<Model, ModelArgs extends unknown[]> (
        ModelClass: {new(...args: ModelArgs): Model},
        component: React.ComponentType<{model: Model}>
    ): CommonOverlay<Model, ModelArgs, typeof ModelClass> {
        const overlay = new CommonOverlay<Model, ModelArgs, typeof ModelClass>(ModelClass, component)
        this.commonOverlays.add(overlay)
        return overlay
    }

    createPage<TRoute extends Route<PageModel>, PageModel> (
        RouteClass: {new(router: RouterService): TRoute},
        component: React.ComponentType<{model: PageModel, route?: Route<PageModel>}>
    ): TRoute {
        const route = new RouteClass(this)
        this.matcher.add(route.paths)
        this.pages.set(route, {route, component})
        return route
    }

    openPage(route: Route<any>, isRedirect = false) {
        const page = this.pages.get(route)
        if (!page)
            return console.warn('Unknown route:', route)
        this.page = page
        if (isRedirect)
            this.setUrl(route.url, true)
    }

    setDefault(openDefault: () => void) {
        this.matcher.setDefault(openDefault)
    }

    isOpened(route: Route<any>) {
        return this.page?.route === route
    }

    clearUrl() {
        this.setUrl('/', true)
    }
}

export let router!: RouterService

export function create() {
    if (router)
        throw new Error('Router already exists!')
    router = new RouterService()
}

